import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart } from '../../components/primitives/PGChart';
import {
  GROUP, BRANCHES, REGIONS, TRENDS, MONTHS,
  BRANCHES_WITH_PHASES, GROUP_PHASE_AVG, computeManagementInsights,
} from '../../utils/orgData';
import { STAGES } from '../../utils/constants';
import { PG } from '../../utils/theme';
import FleetGroupView from './FleetGroupView';

const {
  FiShield, FiClock, FiTool, FiUsers, FiInbox, FiAlertCircle, FiChevronRight,
  FiActivity, FiTruck, FiLayers, FiTrendingUp, FiTarget,
} = FiIcons;

const TabButton = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`h-9 px-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] font-bold transition-all ${
      active
        ? 'bg-[#D71921] text-white'
        : 'bg-transparent text-neutral-500 hover:text-white hover:bg-white/5'
    }`}
  >
    {Icon && <Icon className="text-sm" />}
    {children}
  </button>
);

const slaColor = (v) => (v >= 95 ? PG.emerald : v >= 90 ? PG.amber : PG.red);
const slaTone = (v) => (v >= 95 ? 'ok' : v >= 90 ? 'warn' : 'alert');

export default function OpsSliver() {
  const [view, setView] = useState('jobs');
  const k = GROUP.kpis.ops;

  // Regional SLA sorted ascending (worst at top)
  const regionBars = [...REGIONS].sort((a, b) => a.kpis.ops.slaAdherence - b.kpis.ops.slaAdherence);
  const regionCats = regionBars.map((r) => r.name);
  const regionVals = regionBars.map((r) => ({
    value: +r.kpis.ops.slaAdherence.toFixed(1),
    itemStyle: { color: slaColor(r.kpis.ops.slaAdherence) },
  }));

  // Branch table
  const branchRows = [...BRANCHES].sort((a, b) => b.ops.jobsToday - a.ops.jobsToday);

  // Bottom quartile watchlist — 4 lowest SLA
  const watchlist = [...BRANCHES].sort((a, b) => a.ops.slaAdherence - b.ops.slaAdherence).slice(0, 4);

  return (
    <div className="h-full flex flex-col gap-4 min-h-0">
      {/* View tab bar */}
      <div className="flex items-center flex-shrink-0 border-b border-white/5 pb-3">
        <div className="inline-flex border border-white/10">
          <TabButton active={view === 'jobs'} onClick={() => setView('jobs')} icon={FiActivity}>
            Jobs_Flow
          </TabButton>
          <TabButton active={view === 'phases'} onClick={() => setView('phases')} icon={FiLayers}>
            Phase_Matrix
          </TabButton>
          <TabButton active={view === 'fleet'} onClick={() => setView('fleet')} icon={FiTruck}>
            Fleet_Ops
          </TabButton>
        </div>
        <div className="ml-auto font-mono-tech text-[9px] uppercase tracking-[0.28em] text-neutral-600">
          {view === 'jobs'
            ? 'MONITORING // JOBS & SLA'
            : view === 'phases'
            ? 'MONITORING // PHASE MATRIX & INSIGHTS'
            : 'MONITORING // FLEET & ASSETS'}
        </div>
      </div>

      {view === 'fleet' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <FleetGroupView />
        </div>
      ) : view === 'phases' ? (
        <PhaseMatrixView />
      ) : (
        <>
      {/* KPI row */}
      <div className="grid grid-cols-5 gap-4 flex-shrink-0">
        <KPICard
          title="SLA_Adherence"
          value={`${k.slaAdherence}%`}
          target="≥95%"
          trend={k.slaAdherence >= 95 ? 'up' : 'down'}
          trendValue={k.slaAdherence >= 95 ? '+0.6%' : '-1.2%'}
          icon={FiShield}
          accent={slaColor(k.slaAdherence)}
        />
        <KPICard
          title="Avg_Turnaround"
          value={k.avgTurnaroundMin}
          unit="min"
          target="≤50"
          trend={k.avgTurnaroundMin <= 50 ? 'up' : 'down'}
          trendValue={k.avgTurnaroundMin <= 50 ? '-3m' : '+4m'}
          icon={FiClock}
          accent={PG.cyan}
        />
        <KPICard
          title="First_Time_Fix"
          value={`${k.firstTimeFix}%`}
          target="≥95%"
          trend={k.firstTimeFix >= 95 ? 'up' : 'down'}
          trendValue={k.firstTimeFix >= 95 ? '+0.3%' : '-0.8%'}
          icon={FiTool}
          accent={PG.emerald}
        />
        <KPICard
          title="Technician_Util"
          value={`${k.technicianUtil}%`}
          target="80–90%"
          trend={k.technicianUtil >= 80 ? 'up' : 'down'}
          trendValue={k.technicianUtil >= 80 ? '+2%' : '-4%'}
          icon={FiUsers}
          accent={PG.amber}
        />
        <KPICard
          title="Open_Jobs"
          value={k.openJobs}
          unit={`/ ${k.breachedSlaCount} breached`}
          target="-"
          trend={k.breachedSlaCount === 0 ? 'up' : 'down'}
          trendValue={k.breachedSlaCount === 0 ? 'Clean' : `${k.breachedSlaCount} SLA`}
          icon={FiInbox}
          accent={k.breachedSlaCount === 0 ? PG.emerald : PG.red}
          subdued
        />
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Left column */}
        <div className="col-span-5 flex flex-col gap-4 min-h-0">
          <Panel
            title="SLA_Adherence_By_Region"
            subtitle="Worst first // target 95%"
            className="flex-1 min-h-0"
          >
            <div className="h-full p-2">
              <PGBarChart
                horizontal
                categories={regionCats}
                series={[{
                  name: 'SLA %',
                  data: regionVals,
                }]}
                yFormatter={(v) => `${v}%`}
              />
            </div>
          </Panel>

          <Panel
            title="Bottom_Quartile_Watchlist"
            subtitle="4 lowest-SLA branches"
            className="flex-shrink-0"
          >
            <div className="p-3 space-y-2">
              {watchlist.map((b) => (
                <Link
                  key={b.id}
                  to={`/sliver/ops/branch/${b.id}`}
                  className="flex items-center justify-between px-3 py-2 border border-[#D71921]/30 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FiAlertCircle className="text-[#D71921] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate">
                        {b.name}
                      </div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">
                        {b.region} // {b.country}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StatusPill tone={slaTone(b.ops.slaAdherence)}>
                      {b.ops.slaAdherence}%
                    </StatusPill>
                    <FiChevronRight className="text-neutral-600" />
                  </div>
                </Link>
              ))}
            </div>
          </Panel>
        </div>

        {/* Right column */}
        <div className="col-span-7 flex flex-col gap-4 min-h-0">
          <Panel
            title="Jobs_Today_By_Branch"
            subtitle={`${BRANCHES.length} branches // click to drill`}
            right={<StatusPill tone="info">Live</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Branch</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Jobs_Today</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Open</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Breached</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">SLA%</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Avg_Min</th>
                  </tr>
                </thead>
                <tbody>
                  {branchRows.map((b) => (
                    <tr
                      key={b.id}
                      className="row-clickable border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => { window.location.hash = `#/sliver/ops/branch/${b.id}`; }}
                    >
                      <td className="px-4 py-2">
                        <Link
                          to={`/sliver/ops/branch/${b.id}`}
                          className="block"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white hover:text-[#D71921]">
                            {b.name}
                          </div>
                          <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">
                            {b.region}
                          </div>
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-white">{b.ops.jobsToday}</td>
                      <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">{b.ops.openJobs}</td>
                      <td className="px-3 py-2 text-right font-doto text-base">
                        <span className={b.ops.breachedSlaCount > 0 ? 'text-[#D71921]' : 'text-neutral-500'}>
                          {b.ops.breachedSlaCount}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className="font-doto text-base"
                          style={{ color: slaColor(b.ops.slaAdherence) }}
                        >
                          {b.ops.slaAdherence}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">{b.ops.avgTurnaroundMin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="12_Month_SLA_Trend"
            subtitle="Group SLA% // rolling"
            className="flex-1 min-h-0"
          >
            <div className="h-full p-2">
              <PGLineChart
                categories={MONTHS}
                series={[{
                  name: 'SLA %',
                  data: TRENDS.groupSla.map((v) => +v.toFixed(1)),
                  lineStyle: { color: PG.emerald, width: 2 },
                  itemStyle: { color: PG.emerald },
                  areaStyle: { color: PG.emerald, opacity: 0.08 },
                }]}
                yFormatter={(v) => `${v}%`}
              />
            </div>
          </Panel>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

// ---------- Phase Matrix view ----------

const severityTone = (s) => (s === 'high' ? 'alert' : s === 'medium' ? 'warn' : 'info');
const severityIcon = (s) => (s === 'high' ? FiAlertCircle : s === 'medium' ? FiClock : FiTool);

function PhaseMatrixView() {
  const branchRows = [...BRANCHES_WITH_PHASES].sort((a, b) => {
    const aAvg = avgAdherence(a);
    const bAvg = avgAdherence(b);
    return aAvg - bAvg; // worst at top
  });

  const insights = computeManagementInsights(BRANCHES_WITH_PHASES, STAGES);
  const highCount = insights.filter((i) => i.severity === 'high').length;

  return (
    <>
      {/* Group phase averages strip */}
      <div className="grid grid-cols-7 gap-3 flex-shrink-0">
        {STAGES.map((stage) => {
          const g = GROUP_PHASE_AVG[stage.id];
          const deltaPct = Math.round(((g.actualAvgMin - g.slaMin) / g.slaMin) * 100);
          return (
            <KPICard
              key={stage.id}
              title={stage.name}
              value={`${g.adherencePct}%`}
              unit={`${g.actualAvgMin}m`}
              target={`SLA ${g.slaMin}m`}
              trend={deltaPct <= 0 ? 'up' : 'down'}
              trendValue={deltaPct <= 0 ? 'Inside' : `+${deltaPct}%`}
              icon={FiTarget}
              accent={slaColor(g.adherencePct)}
              subdued
            />
          );
        })}
      </div>

      {/* Body: matrix + insights */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="col-span-8 flex flex-col min-h-0">
          <Panel
            title="Branch_x_Phase_Adherence_Matrix"
            subtitle={`${branchRows.length} branches // 7 phases // worst first`}
            right={<StatusPill tone="info">Group_Row_Pinned</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                  <tr className="border-b border-white/10">
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 sticky left-0 bg-[#0A0A0A]">Branch</th>
                    {STAGES.map((s) => (
                      <th key={s.id} className="px-2 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-center">
                        {s.name}
                      </th>
                    ))}
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-center">Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Group average row pinned */}
                  <tr className="border-b border-[#D71921]/40 bg-[#D71921]/5">
                    <td className="px-3 py-2 sticky left-0 bg-[#0A0A0A]">
                      <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#D71921] font-bold">
                        Group_Avg
                      </div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">benchmark</div>
                    </td>
                    {STAGES.map((s) => {
                      const g = GROUP_PHASE_AVG[s.id];
                      return (
                        <td key={s.id} className="px-2 py-2 text-center">
                          <span className="font-doto text-sm" style={{ color: slaColor(g.adherencePct) }}>
                            {g.adherencePct}%
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center font-doto text-sm text-white">
                      {groupOverallAdherence()}%
                    </td>
                  </tr>
                  {branchRows.map((b) => {
                    const overall = +avgAdherence(b).toFixed(1);
                    return (
                      <tr
                        key={b.id}
                        className="row-clickable border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => { window.location.hash = `#/sliver/ops/branch/${b.id}`; }}
                      >
                        <td className="px-3 py-2 sticky left-0 bg-[#0A0A0A]">
                          <Link
                            to={`/sliver/ops/branch/${b.id}`}
                            className="block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white hover:text-[#D71921]">
                              {b.name}
                            </div>
                            <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">
                              {b.region}
                            </div>
                          </Link>
                        </td>
                        {STAGES.map((s) => {
                          const p = b.phasePerformance[s.id];
                          return (
                            <td key={s.id} className="px-2 py-2 text-center">
                              <div
                                className="inline-flex flex-col items-center justify-center px-2 py-1 border"
                                style={{
                                  borderColor: `${slaColor(p.adherencePct)}40`,
                                  background: `${slaColor(p.adherencePct)}10`,
                                }}
                              >
                                <span className="font-doto text-sm leading-none" style={{ color: slaColor(p.adherencePct) }}>
                                  {p.adherencePct}%
                                </span>
                                <span className="font-mono-tech text-[8px] text-neutral-600 mt-0.5">
                                  {p.actualAvgMin}m
                                </span>
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-center">
                          <span className="font-doto text-sm" style={{ color: slaColor(overall) }}>
                            {overall}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="col-span-4 flex flex-col min-h-0">
          <Panel
            title="Management_Insights"
            subtitle={`${insights.length} actions // ${highCount} high severity`}
            tone={highCount > 0 ? 'alert' : 'warn'}
            right={<StatusPill tone={highCount > 0 ? 'alert' : 'warn'} pulse={highCount > 0}>Action</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar p-3 space-y-2">
              {insights.length === 0 && (
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-600 text-center py-10">
                  No Phase Breaches Detected
                </div>
              )}
              {insights.map((row, i) => {
                const Icon = severityIcon(row.severity);
                return (
                  <Link
                    key={`${row.branchId}-${row.phaseId}-${i}`}
                    to={`/sliver/ops/branch/${row.branchId}`}
                    className="block p-3 border border-white/10 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="flex-shrink-0" style={{ color: row.severity === 'high' ? PG.red : PG.amber }} />
                        <div className="font-mono-tech text-[10px] uppercase tracking-wider text-white truncate">
                          {row.branchName}
                        </div>
                      </div>
                      <StatusPill tone={severityTone(row.severity)}>
                        {row.severity}
                      </StatusPill>
                    </div>
                    <div className="flex items-center gap-3 mb-2 font-mono-tech text-[10px] uppercase tracking-wider">
                      <span className="text-neutral-400">{row.phaseName}</span>
                      <span className="text-neutral-700">//</span>
                      <span className="text-[#D71921]">+{row.deltaPct}% over SLA</span>
                      <span className="text-neutral-700">//</span>
                      <span className="text-neutral-500">{row.adherencePct}% adh</span>
                    </div>
                    <div className="font-sans text-[11px] text-neutral-300 leading-snug">
                      {row.recommendation}
                    </div>
                  </Link>
                );
              })}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}

function avgAdherence(branch) {
  const vals = STAGES.map((s) => branch.phasePerformance[s.id].adherencePct);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function groupOverallAdherence() {
  const vals = STAGES.map((s) => GROUP_PHASE_AVG[s.id].adherencePct);
  return +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}
